import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { WelcomeIntro } from "./components/WelcomeIntro";
import { MobileContactBar } from "./components/MobileContactBar";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import { parseLocalized, localize, isLocalizable } from "./lib/routes";
import { CookieProvider, CookieBanner } from "./components/CookieBanner";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { Chatbot } from "./components/Chatbot";
import { ScrollToTop } from "./components/ScrollToTop";
import HomePage from "./pages/HomePage";
import ServicesPage from "./pages/ServicesPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import FAQPage from "./pages/FAQPage";
// import NewsPage from "./pages/NewsPage"; // Disabilitato temporaneamente
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import LoginPage from "./pages/LoginPage";
import ClientAreaPage from "./pages/ClientAreaPage";
import AdminPage from "./pages/AdminPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import ServiceDetailPage from "./pages/ServiceDetailPage";
import SetPasswordPage from "./pages/SetPasswordPage";
import AccessRequestReview from "./pages/AccessRequestReview";
import AppointmentResponsePage from "./pages/AppointmentResponsePage";

// Pages are defined with Italian paths; localized URLs are mapped onto them.
const PublicRoutes = ({ location, lang }) => (
  <Routes location={location}>
    <Route path="/" element={<HomePage />} />
    <Route path="/servizi" element={<ServicesPage />} />
    <Route path="/servizi/:slug" element={<ServiceDetailPage />} />
    <Route path="/chi-siamo" element={<AboutPage />} />
    <Route path="/contatti" element={<ContactPage />} />
    <Route path="/faq" element={<FAQPage />} />
    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
    <Route path="/cookie-policy" element={<CookiePolicyPage />} />
    <Route path="*" element={<Navigate to={`/${lang}`} replace />} />
  </Routes>
);

const LocalizedPage = () => {
  const location = useLocation();
  const { language, setLanguage } = useLanguage();
  const parsed = parseLocalized(location.pathname);
  useEffect(() => {
    if (parsed && parsed.lang !== language) setLanguage(parsed.lang);
  }, [parsed?.lang]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!parsed) return <Navigate to={`/${language}`} replace />;
  const pathname = `/${parsed.lang}${parsed.itPath === '/' ? '' : parsed.itPath}`;
  return <PublicRoutes location={{ ...location, pathname }} lang={parsed.lang} />;
};

const LegacyRedirect = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const target = isLocalizable(location.pathname) ? localize(location.pathname, language) : `/${language}`;
  return <Navigate to={target + location.search + location.hash} replace />;
};

// Layout wrapper to conditionally show navbar/footer
const Layout = ({ children }) => {
  const location = useLocation();
  const isAdminPage = location.pathname === '/admin';
  const [showIntro] = useState(() => !/^\/(admin|area-clienti|imposta-password)/.test(window.location.pathname));
  
  return (
    <>
      {showIntro && <WelcomeIntro />}
      {!isAdminPage && <Navbar />}
      <main>{children}</main>
      {!isAdminPage && <Footer />}
      {!isAdminPage && <div className="md:hidden h-16" aria-hidden="true" />}
      {!isAdminPage && <MobileContactBar />}
      {!isAdminPage && <Chatbot />}
    </>
  );
};

function App() {
  return (
    <LanguageProvider>
      <CookieProvider>
        <div className="App">
          <BrowserRouter>
            <ScrollToTop />
            <Layout>
              <Routes>
                <Route path="/" element={<LegacyRedirect />} />
                <Route path="/servizi/*" element={<LegacyRedirect />} />
                <Route path="/chi-siamo" element={<LegacyRedirect />} />
                <Route path="/contatti" element={<LegacyRedirect />} />
                <Route path="/faq" element={<LegacyRedirect />} />
                <Route path="/privacy-policy" element={<LegacyRedirect />} />
                <Route path="/cookie-policy" element={<LegacyRedirect />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/imposta-password" element={<SetPasswordPage />} />
                <Route path="/area-clienti" element={<ClientAreaPage />} />
                <Route path="/area-clienti/pagamenti/success" element={<PaymentSuccess />} />
                <Route path="/area-clienti/pagamenti/cancel" element={<PaymentCancel />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/richiesta-accesso/:id" element={<AccessRequestReview />} />
                <Route path="/appuntamento/:id" element={<AppointmentResponsePage />} />
                <Route path="/:lang/*" element={<LocalizedPage />} />
                <Route path="*" element={<LegacyRedirect />} />
              </Routes>
            </Layout>
            <CookieBanner />
          </BrowserRouter>
        </div>
      </CookieProvider>
    </LanguageProvider>
  );
}

export default App;
